import React, { useState, useEffect, useCallback } from "react";
import api from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import PageHeader from "../components/PageHeader";
import { UserPlus, Trash2, Calendar, Search, Edit2, X, Check, CheckCircle, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

const CustomSelect = ({ value, options, onChange, label, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">{label}</label>}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-5 py-3 bg-gray-50 border border-transparent rounded-2xl transition-all duration-300 font-bold text-xs ${isOpen ? 'bg-white border-gray-900 ring-4 ring-gray-900/5 shadow-sm' : 'hover:bg-gray-100'}`}
      >
        <span className="text-gray-900 truncate">{selectedOption?.label || "Select Option"}</span>
        <ChevronDown size={16} className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-gray-900' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl z-[100] overflow-hidden animate-slideIn">
          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {options.map((opt) => (
              <div
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`px-5 py-3 text-xs font-bold transition-colors cursor-pointer flex items-center justify-between ${value === opt.value ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`}
              >
                {opt.label}
                {value === opt.value && <Check size={14} className="text-white" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MultiSelect = ({ value = [], options, onChange, label, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const selectedLabels = options
    .filter(opt => value.includes(opt.value))
    .map(opt => opt.label);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">{label}</label>}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-5 py-3 bg-gray-50 border border-transparent rounded-2xl transition-all duration-300 font-bold text-xs ${isOpen ? 'bg-white border-gray-900 ring-4 ring-gray-900/5 shadow-sm' : 'hover:bg-gray-100'}`}
      >
        <span className="text-gray-900 truncate">
          {selectedLabels.length > 0 ? selectedLabels.join(", ") : "No Bots Assigned"}
        </span>
        <div className="flex items-center gap-2">
          {selectedLabels.length > 0 && (
            <span className="bg-gray-900 text-white text-[10px] px-2 py-0.5 rounded-full">
              {selectedLabels.length}
            </span>
          )}
          <ChevronDown size={16} className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-gray-900' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl z-[100] overflow-hidden animate-slideIn">
          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {options.map((opt) => (
              <div
                key={opt.value}
                onClick={() => toggleOption(opt.value)}
                className={`px-5 py-3 text-xs font-bold transition-colors cursor-pointer flex items-center justify-between ${value.includes(opt.value) ? "bg-gray-50 text-gray-900" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${value.includes(opt.value) ? "bg-gray-900 border-gray-900" : "bg-white border-gray-200"}`}>
                    {value.includes(opt.value) && <Check size={12} className="text-white" />}
                  </div>
                  {opt.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const HumanAgents = ({ aiAgents = [] }) => {
  const { showToast } = useToast();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const USERS_PER_PAGE = 10;

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "human_agent",
    assigned_ai_agents: []
  });

  const [editFormData, setEditFormData] = useState({
    id: null,
    username: "",
    email: "",
    password: "",
    role: "human_agent",
    assigned_ai_agents: [],
    is_active: true
  });

  const [billingInfo, setBillingInfo] = useState({
    usedUserSlots: 0,
    maxUserSlots: 3,
    planName: "Starter"
  });

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.request("/users");
      // Filter users with role 'human_agent' or 'admin' since admin acts as a human agent
      const humanAgentsList = Array.isArray(data) ? data.filter(u => u.role === "human_agent" || u.role === "admin") : [];
      setUsers(humanAgentsList);

      setBillingInfo(prev => ({
        ...prev,
        usedUserSlots: humanAgentsList.filter(u => u.is_active !== false).length
      }));
    } catch (error) {
      console.error("Error fetching human agents:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const loadBillingInfo = async () => {
      try {
        const userStr = sessionStorage.getItem("user");
        const user = userStr ? JSON.parse(userStr) : null;
        const tenantId = user?.tenant_id || "admin";

        const subRes = await api.request(`/api/subscription/${tenantId}`);
        let maxUserSlots = 0;
        let planName = "No Plan";
        if (subRes && subRes.success && subRes.data) {
          const planId = subRes.data.plan_id.toLowerCase();
          planName = subRes.data.plan_name;
          const PLAN_PRESETS = {
            free_trial: 1,
            starter: 3,
            growth: 5,
            professional: 7,
            enterprise: 999
          };
          maxUserSlots = (PLAN_PRESETS[planId] || 0) + (subRes.data.extra_human_agents || 0);
        }
        setBillingInfo(prev => ({ ...prev, maxUserSlots, planName }));
      } catch (err) {
        console.error("Failed to load billing limits for human agents:", err);
      }
    };

    loadBillingInfo();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await api.request("/users", {
        method: "POST",
        body: JSON.stringify(formData)
      });

      showToast(`Human Agent account created for ${formData.username}`, "success");
      setShowAddModal(false);
      setFormData({
        username: "",
        email: "",
        password: "",
        role: "human_agent",
        assigned_ai_agents: []
      });
      fetchUsers();
    } catch (error) {
      console.error("Agent creation failed:", error);
      showToast(error.message || "Failed to create human agent", "error");
    }
  };

  const handleEditClick = (user) => {
    setEditFormData({
      id: user.id,
      username: user.username,
      email: user.email,
      password: "",
      role: user.role,
      assigned_ai_agents: user.assigned_ai_agents || [],
      is_active: user.is_active !== false
    });
    setShowEditModal(true);
  };

  const handleEditUserSubmit = async (e) => {
    e.preventDefault();
    try {
      const updates = {
        username: editFormData.username,
        email: editFormData.email,
        role: editFormData.role,
        assigned_ai_agents: editFormData.assigned_ai_agents,
        is_active: editFormData.is_active
      };

      if (editFormData.password) {
        updates.password = editFormData.password;
      }

      await api.request(`/users/${editFormData.id}`, {
        method: "PATCH",
        body: JSON.stringify(updates)
      });

      showToast("Agent account updated successfully", "success");
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      console.error("Agent update failed:", error);
      showToast(error.message || "Failed to update agent account", "error");
    }
  };

  const handleUpdateUserStatus = async (userId, statusUpdates) => {
    try {
      await api.request(`/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify(statusUpdates)
      });
      showToast("Agent status updated", "success");
      fetchUsers();
    } catch (error) {
      console.error("Status update failed:", error);
      showToast("Failed to update status", "error");
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      await api.request(`/users/${userToDelete.id}`, {
        method: "DELETE"
      });
      showToast("Agent account deleted successfully", "success");
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error("Agent deletion failed:", error);
      showToast("Failed to delete agent", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredUsers = users
    .filter(user =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.username.localeCompare(b.username));

  const agentOptions = aiAgents.map(p => ({ value: p.id, label: p.name }));

  // Pagination Logic
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn">
      <div className="max-w-6xl mx-auto">
        <PageHeader
          title="Human Agents"
          description="Provision live human agent seats, configure access control, and manage system login permissions."
          actions={
            <div className="flex items-center gap-4">
              <span className="text-xs font-bold text-gray-500">
                Seats Used: {billingInfo.usedUserSlots} / {billingInfo.maxUserSlots ?? "—"}
              </span>
              <button
                onClick={() => {
                  if (billingInfo.usedUserSlots >= billingInfo.maxUserSlots) {
                    showToast(`You have reached the maximum limit of ${billingInfo.maxUserSlots} active seats on your plan. Upgrade your plan to add more.`, "error");
                    return;
                  }
                  setShowAddModal(true);
                }}
                className="flex items-center justify-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-2xl hover:bg-gray-800 transition-all font-black text-sm uppercase shadow-xl shadow-gray-200"
              >
                <UserPlus size={18} />
                Add Human Agent
              </button>
            </div>
          }
        />

        {/* Limit Cards */}
        <div className="max-w-2xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 animate-slideIn">
            <div className="bg-white rounded-[24px] p-6 border border-gray-100 shadow-md shadow-gray-100/30 flex flex-col items-center justify-center text-center">
              <div className="space-y-1">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Human Agents</p>
                <p className="text-xl font-black text-gray-900 leading-snug">
                  {billingInfo.planName?.toLowerCase() === "enterprise"
                    ? billingInfo.planName
                    : <>{billingInfo.usedUserSlots} <span className="text-gray-300 font-normal">/</span> {billingInfo.maxUserSlots ?? "—"}</>
                  }
                </p>
              </div>
            </div>
            <div className="bg-white rounded-[24px] p-6 border border-gray-100 shadow-md shadow-gray-100/30 flex flex-col items-center justify-center text-center">
              <div className="space-y-1">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Subscription Plan</p>
                <p className="text-xl font-black text-gray-900 leading-snug">{billingInfo.planName}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Human Agent Seats Table */}
        <div className="bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 animate-slideIn">
          <div className="p-8 border-b border-gray-50 bg-gray-50/30 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by username or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-6 py-3 bg-white border border-gray-100 rounded-2xl text-xs font-bold tracking-tight focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-900 transition-all shadow-sm"
              />
            </div>
          </div>

          <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
            <table className="w-full text-left">
              <thead className="bg-gray-50/50 sticky top-0 z-10 shadow-sm backdrop-blur-md">
                <tr>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Human Agent ID</th>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Name</th>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Email</th>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Assigned AI Agents</th>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Created At</th>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading ? (
                  <tr>
                    <td colSpan="7" className="px-8 py-24 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-10 h-10 border-4 border-gray-100 border-t-gray-900 rounded-full animate-spin"></div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Loading Human Agents...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-8 py-24 text-center">
                      <div className="w-20 h-20 bg-gray-50 rounded-[28px] border border-gray-100 flex items-center justify-center mb-6 mx-auto">
                        <Search size={32} className="text-gray-300" />
                      </div>
                      <h3 className="text-xs font-bold uppercase tracking-widest text-gray-900 mb-2">No Human Agents Found</h3>
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50/50 transition-all group">
                      <td className="px-6 py-6">
                        <span className="text-[10px] text-gray-500 font-mono tracking-wider font-bold">
                          {user.human_agent_id || user.ai_agent_id || "—"}
                        </span>
                      </td>
                      <td className="px-6 py-6">
                        <span className="text-sm font-black text-gray-900 tracking-tight">{user.username}</span>
                      </td>
                      <td className="px-6 py-6 text-xs text-gray-500 font-bold tracking-tight">
                        {user.email}
                      </td>
                      <td className="px-6 py-6 min-w-[200px]">
                        <div className="flex flex-wrap gap-1">
                          {user.role === 'admin' ? (
                            <span className="text-[10px] font-black text-[#83bbe1] uppercase tracking-widest flex items-center gap-2">
                              Full System Access (ALL)
                            </span>
                          ) : user.assigned_ai_agents && user.assigned_ai_agents.length > 0 ? (
                            user.assigned_ai_agents.map(botId => {
                              const bot = aiAgents.find(p => p.id === botId);
                              return (
                                <span key={botId} className="px-2 py-0.5 bg-gray-100 rounded-lg text-[9px] font-bold text-gray-600">
                                  {bot?.name || `Bot #${botId}`}
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-[10px] font-bold text-gray-300 italic">No Bots Assigned</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-6">
                        <button
                          disabled={user.role === "admin"}
                          onClick={() => handleUpdateUserStatus(user.id, { is_active: user.is_active === false })}
                          className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white flex-shrink-0 ${user.is_active !== false ? 'border-[#83bbe1]' : 'border-gray-200'} ${user.role === "admin" ? "opacity-50 cursor-not-allowed" : ""}`}
                          title={user.role === "admin" ? "Administrator status cannot be modified" : (user.is_active !== false ? "Suspend Seat" : "Activate Seat")}
                        >
                          <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${user.is_active !== false ? 'left-[22px] bg-[#83bbe1]' : 'left-[2px] bg-gray-300'}`} />
                        </button>
                      </td>
                      <td className="px-6 py-6">
                        <div className="flex flex-col text-[10px] font-bold text-gray-400 tracking-wider">
                          <span className="flex items-center gap-1.5 whitespace-nowrap">
                            <Calendar size={12} className="text-gray-400" />
                            {(() => {
                              let dateStr = user.created_at;
                              if (dateStr && typeof dateStr === 'string') {
                                if (!dateStr.includes('T') && dateStr.includes(' ')) {
                                  dateStr = dateStr.replace(' ', 'T');
                                }
                                if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('GMT')) {
                                  dateStr = dateStr + 'Z';
                                }
                              }
                              const date = new Date(dateStr);
                              return date.toLocaleString([], {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              });
                            })()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            disabled={user.role === "admin"}
                            onClick={() => handleEditClick(user)}
                            className={`p-2.5 text-gray-400 bg-white border border-gray-100 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-200 rounded-xl transition-all shadow-sm flex items-center justify-center ${user.role === "admin" ? "opacity-50 cursor-not-allowed" : ""}`}
                            title={user.role === "admin" ? "Admin profile cannot be modified here" : "Edit Permissions"}
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            disabled={user.role === "admin"}
                            onClick={() => setUserToDelete(user)}
                            className={`p-2.5 text-gray-400 bg-white border border-gray-100 hover:text-red-500 hover:bg-red-50 hover:border-red-100 rounded-xl transition-all shadow-sm flex items-center justify-center ${user.role === "admin" ? "opacity-50 cursor-not-allowed" : ""}`}
                            title={user.role === "admin" ? "Admin accounts cannot be deleted" : "Delete Human Agent Account"}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-8 py-6 border-t border-gray-50 flex items-center justify-between bg-gray-50/30">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Showing <span className="text-gray-900">{(currentPage - 1) * USERS_PER_PAGE + 1}</span> to <span className="text-gray-900">{Math.min(currentPage * USERS_PER_PAGE, filteredUsers.length)}</span> of <span className="text-gray-900">{filteredUsers.length}</span> human agents
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 text-gray-400 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="flex items-center gap-1">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`w-8 h-8 rounded-lg text-[10px] font-black transition-all ${currentPage === i + 1 ? "bg-gray-900 text-white shadow-lg" : "text-gray-400 hover:bg-gray-100"}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 text-gray-400 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Provisioning Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-[32px] w-full max-w-xl shadow-2xl animate-scaleIn border border-gray-100/50">
            <div className="px-8 py-5 border-b border-gray-50 flex justify-between items-center bg-gray-50/20 rounded-t-[32px]">
              <div>
                <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase">Initialize Human Agent Seat</h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-0.5">Provisioning Protocol</p>
              </div>
              <button onClick={() => setShowAddModal(false)} className="p-2 text-gray-400 hover:text-gray-900 hover:bg-white rounded-xl transition-all">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAddUser} className="p-7 space-y-5">
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Username</label>
                  <input
                    required
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="e.g. agent_alex"
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Email</label>
                  <input
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="alex@easychat.com"
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Password</label>
                <input
                  required
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Min 8 characters"
                  className="w-full px-5 py-3 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all tracking-widest"
                />
              </div>

              <div className="space-y-2">
                <MultiSelect
                  label="Assigned Bots (Access Control)"
                  value={formData.assigned_ai_agents}
                  options={agentOptions}
                  onChange={(val) => setFormData({ ...formData, assigned_ai_agents: val })}
                />
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-gray-900 text-white font-black text-xs uppercase tracking-[0.2em] rounded-2xl hover:bg-gray-800 transition-all shadow-xl active:scale-95 flex items-center justify-center gap-3 mt-4"
              >
                <CheckCircle size={18} />
                Confirm Provisioning
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Credentials Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-[32px] w-full max-w-xl shadow-2xl animate-scaleIn border border-gray-100/50">
            <div className="px-8 py-5 border-b border-gray-50 flex justify-between items-center bg-gray-50/20 rounded-t-[32px]">
              <div>
                <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase">Edit Human Agent Seat</h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-0.5">Permissions Protocol</p>
              </div>
              <button onClick={() => setShowEditModal(false)} className="p-2 text-gray-400 hover:text-gray-900 hover:bg-white rounded-xl transition-all">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleEditUserSubmit} className="p-7 space-y-5">
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Username</label>
                  <input
                    required
                    type="text"
                    value={editFormData.username}
                    onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                    placeholder="e.g. agent_alex"
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Email</label>
                  <input
                    required
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    placeholder="alex@easychat.com"
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Password</label>
                  <input
                    type="password"
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                    placeholder="Leave blank to keep same"
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all tracking-widest"
                  />
                </div>
                <CustomSelect
                  label="Status"
                  value={editFormData.is_active}
                  options={[
                    { value: true, label: "Active" },
                    { value: false, label: "Suspended" }
                  ]}
                  onChange={(val) => setEditFormData({ ...editFormData, is_active: val })}
                />
              </div>

              <div className="space-y-2">
                <MultiSelect
                  label="Assigned Bots"
                  value={editFormData.assigned_ai_agents}
                  options={agentOptions}
                  onChange={(val) => setEditFormData({ ...editFormData, assigned_ai_agents: val })}
                />
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-gray-900 text-white font-black text-xs uppercase tracking-[0.2em] rounded-2xl hover:bg-gray-800 transition-all shadow-xl active:scale-95 flex items-center justify-center gap-3 mt-4"
              >
                <CheckCircle size={18} />
                Update Seat Protocol
              </button>
            </form>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={userToDelete !== null}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDeleteUser}
        isLoading={isDeleting}
        title="Delete Human Agent Seat?"
        message={`This action will permanently delete human agent account '${userToDelete?.username}'. Are you prepared to proceed?`}
      />
    </div>
  );
};

export default HumanAgents;
